
import { Waybill, WaybillStatus, Vehicle, Tire, Employee, WaybillBlank } from '../../types';
import { createRepo, ListQuery } from '../repo';
import { DB_KEYS } from '../dbKeys';
import { auditBusiness } from '../auditBusiness';
import { useBlankForWaybill, releaseBlank, markBlankAsSpoiled, reserveBlank } from './blanks';
import { adjustFuelCardBalance } from './employees';
import { getSeasonSettings, getAppSettings, isWinterDate } from './settings';
import { calculateStats } from '../../utils/waybillCalculations';
import { broadcast } from '../bus';
import { TransactionManager } from '../transactionService';
import { generateNextNumber } from '../sequenceService';
import { withResourceLock } from '../../utils/concurrency';
import { checkPeriodLock } from './integrity';
import { waybillRepository } from '../repositories/waybillRepository';
import { stockLedgerService } from '../stockLedgerService';
import { fuelCardService } from '../fuelCardService';
import { stockLocationRepository } from '../repositories/stockLocationRepository';

// --- DOMAIN IMPORTS ---
import { calculateNormConsumption, calculateFuelEnd, WaybillRules } from '../../domain/waybill';

// --- REPOSITORY ---
const vehicleRepo = createRepo<Vehicle>(DB_KEYS.VEHICLES);
const tireRepo = createRepo<Tire>(DB_KEYS.TIRES);
const employeeRepo = createRepo<Employee>(DB_KEYS.EMPLOYEES);
const blankRepo = createRepo<WaybillBlank>(DB_KEYS.WAYBILL_BLANKS);

export interface RecalculationChange {
    field: string;
    oldVal: string | number;
    newVal: string | number;
}

export interface RecalculationLogEntry {
    id: string;
    number: string;
    date: string;
    changes: RecalculationChange[];
    warnings: string[];
}

// === LEGACY ADAPTERS (Migrated to IDB) ===

export const getWaybills = async () => {
    return await waybillRepository.getAll();
};

export const fetchWaybills = async (q: ListQuery) => {
    const data = await waybillRepository.list({
        page: q.page,
        pageSize: q.pageSize,
        filters: q.filters,
        sortBy: q.sortBy,
        sortDir: q.sortDir
    });
    // Approximate count or real count if needed
    const total = await waybillRepository.count(q.filters);
    return {
        data,
        page: q.page || 1,
        pageSize: q.pageSize || 1000,
        total,
        hasMore: data.length === (q.pageSize || 1000)
    };
};

export const fetchWaybillById = (id: string) => waybillRepository.getById(id);

export interface WaybillFilters {
    dateFrom?: string;
    dateTo?: string;
    status?: WaybillStatus | '';
    vehicleId?: string;
    driverId?: string;
}

export const fetchWaybillsInfinite = async (params: {
    page: number;
    pageSize: number;
    filters?: WaybillFilters;
    sort?: { key: string; direction: 'asc' | 'desc' }
}) => {
    const data = await waybillRepository.list({
        page: params.page,
        pageSize: params.pageSize,
        filters: params.filters,
        sortBy: params.sort?.key,
        sortDir: params.sort?.direction
    });

    const total = await waybillRepository.count(params.filters);

    return {
        data,
        page: params.page,
        pageSize: params.pageSize,
        total,
        hasMore: data.length === params.pageSize
    };
};

// NEW: Paged fetch using optimized IDB cursor repository
export const fetchWaybillsPaged = async (params: {
    page: number;
    pageSize: number;
    filters?: WaybillFilters;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
}) => {
    const { page, pageSize, filters, sortBy, sortDir } = params;

    // Parallel fetch: data and count
    const [data, total] = await Promise.all([
        waybillRepository.list({
            page,
            pageSize,
            filters: {
                vehicleId: filters?.vehicleId,
                dateFrom: filters?.dateFrom,
                dateTo: filters?.dateTo,
                status: filters?.status as string,
                driverId: filters?.driverId
            },
            sortBy,
            sortDir
        }),
        waybillRepository.count({
            vehicleId: filters?.vehicleId,
            dateFrom: filters?.dateFrom,
            dateTo: filters?.dateTo,
            status: filters?.status as string,
            driverId: filters?.driverId
        })
    ]);

    return {
        data,
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize)
    };
};

// --- Summary/Totals Aggregation ---

export interface WaybillSummary {
    totalMileage: number;
    totalFuelFilled: number;
    totalFuelPlanned: number;
    avgRate: number;
    count: number;
    firstOdometerStart: number | null;
    lastOdometerEnd: number | null;
    firstFuelAtStart: number | null;
    lastFuelAtEnd: number | null;
    firstValidFrom: string | null;
    lastValidTo: string | null;
}

export const fetchWaybillsSummary = async (filters?: WaybillFilters): Promise<WaybillSummary> => {
    const all = await waybillRepository.list({
        page: 1,
        pageSize: 100000,
        filters: {
            vehicleId: filters?.vehicleId,
            dateFrom: filters?.dateFrom,
            dateTo: filters?.dateTo,
            status: filters?.status as string,
            driverId: filters?.driverId
        },
        sortBy: 'validFrom',
        sortDir: 'asc'
    });

    if (all.length === 0) {
        return {
            totalMileage: 0,
            totalFuelFilled: 0,
            totalFuelPlanned: 0,
            avgRate: 0,
            count: 0,
            firstOdometerStart: null,
            lastOdometerEnd: null,
            firstFuelAtStart: null,
            lastFuelAtEnd: null,
            firstValidFrom: null,
            lastValidTo: null
        };
    }

    let totalMileage = 0;
    let totalFuelFilled = 0;
    let totalFuelPlanned = 0;
    let rateSum = 0;
    let rateCount = 0;

    for (const w of all) {
        const mileage = (w.odometerEnd && w.odometerStart) ? w.odometerEnd - w.odometerStart : 0;
        totalMileage += mileage;
        totalFuelFilled += w.fuelFilled || 0;
        totalFuelPlanned += w.fuelPlanned || 0;

        // Rate is not stored on the waybill itself — we pass it as 0 here.
        // The component will compute avgRate from its enriched data.
        // However, for a simpler approach, fuelPlanned / mileage gives effective rate.
    }

    const first = all[0];
    const last = all[all.length - 1];

    return {
        totalMileage,
        totalFuelFilled,
        totalFuelPlanned,
        avgRate: 0, // Computed on the client side with season settings
        count: all.length,
        firstOdometerStart: first.odometerStart ?? null,
        lastOdometerEnd: last.odometerEnd ?? null,
        firstFuelAtStart: first.fuelAtStart ?? null,
        lastFuelAtEnd: last.fuelAtEnd ?? null,
        firstValidFrom: first.validFrom ?? null,
        lastValidTo: last.validTo ?? null
    };
};

// --- Chain Recalculation Logic ---
const recalculateWaybillChain = async (modifiedWaybill: Waybill) => {
    try {
        const allResult = await waybillRepository.list({
            filters: { vehicleId: modifiedWaybill.vehicleId },
            pageSize: 10000
        });

        // waybillRepository.list returns Waybill[]
        const sortedChain = allResult.sort((a, b) => {
            const timeA = new Date(a.validFrom).getTime();
            const timeB = new Date(b.validFrom).getTime();
            if (timeA !== timeB) return timeA - timeB;
            return a.number.localeCompare(b.number);
        });

        const currentIndex = sortedChain.findIndex(w => w.id === modifiedWaybill.id);
        if (currentIndex === -1 || currentIndex === sortedChain.length - 1) return;

        const vehicle = await vehicleRepo.getById(modifiedWaybill.vehicleId);
        if (!vehicle) return;
        const seasonSettings = await getSeasonSettings();

        let previousWaybill = modifiedWaybill;
        const updates: Waybill[] = [];

        for (let i = currentIndex + 1; i < sortedChain.length; i++) {
            const current = { ...sortedChain[i] };

            if (current.status !== WaybillStatus.DRAFT) {
                break;
            }

            current.odometerStart = previousWaybill.odometerEnd ?? previousWaybill.odometerStart;
            current.fuelAtStart = previousWaybill.fuelAtEnd ?? 0;

            const dayMode = current.date === current.validTo.split('T')[0] ? 'single' : 'multi';
            const method = current.calculationMethod || 'by_total';

            const stats = calculateStats(
                current.routes,
                vehicle,
                seasonSettings,
                current.date,
                dayMode,
                method
            );

            current.odometerEnd = current.odometerStart + stats.distance;
            current.fuelPlanned = stats.consumption;

            const fuelFilled = current.fuelFilled || 0;
            // Use Domain Logic for fuel end
            current.fuelAtEnd = calculateFuelEnd(current.fuelAtStart, fuelFilled, stats.consumption);

            updates.push(current);
            previousWaybill = current;
        }

        if (updates.length > 0) {
            console.log(`[Chain] Updating ${updates.length} waybills in bulk...`);
            await waybillRepository.updateBulk(updates);
            broadcast('waybills');
        }

    } catch (e) {
        console.error('Critical error in chain recalculation:', e);
    }
};

export const recalculateDraftsChain = async (vehicleId: string, fromDate: string): Promise<{ count: number; logs: RecalculationLogEntry[] }> => {
    console.log(`[recalculateDraftsChain] Starting for vehicle ${vehicleId}, fromDate: ${fromDate}`);

    const allResult = await waybillRepository.list({
        filters: { vehicleId },
        pageSize: 10000
    });

    console.log(`[recalculateDraftsChain] Total waybills for vehicle: ${allResult.length}`);

    // 1. Находим "якорь" - последний проведенный ПЛ до начала пересчета
    const postedBefore = allResult
        .filter(w => w.status === WaybillStatus.POSTED && w.date < fromDate)
        .sort((a, b) => {
            // Сортировка по дате (YYYY-MM-DD строки сортируются корректно)
            if (a.date !== b.date) return b.date.localeCompare(a.date);
            // При равных датах — по времени validTo
            return new Date(b.validTo).getTime() - new Date(a.validTo).getTime();
        });

    const anchor = postedBefore[0];

    console.log(`[recalculateDraftsChain] Posted before ${fromDate}: ${postedBefore.length}`);
    console.log(`[recalculateDraftsChain] Anchor: ${anchor?.number} (${anchor?.date}), odometerEnd: ${anchor?.odometerEnd}, fuelEnd: ${anchor?.fuelAtEnd}`);

    const vehicle = await vehicleRepo.getById(vehicleId);
    if (!vehicle) throw new Error('Vehicle not found');

    // 2. Определяем список черновиков для пересчета
    const draftsToProcess = allResult
        .filter(w => w.status === WaybillStatus.DRAFT && w.date >= fromDate)
        .sort((a, b) => {
            // Сначала по дате
            if (a.date !== b.date) return a.date.localeCompare(b.date);
            // Затем по времени выезда
            const timeA = new Date(a.validFrom).getTime();
            const timeB = new Date(b.validFrom).getTime();
            if (timeA !== timeB) return timeA - timeB;
            return a.number.localeCompare(b.number);
        });

    console.log(`[recalculateDraftsChain] Drafts to process: ${draftsToProcess.map(d => d.number).join(', ')}`);

    if (draftsToProcess.length === 0) return { count: 0, logs: [] };

    // 3. Инициализируем стартовые значения
    let startOdometer = anchor ? (anchor.odometerEnd ?? 0) : (draftsToProcess[0].odometerStart ?? vehicle.mileage);
    let startFuel = anchor ? (anchor.fuelAtEnd ?? 0) : (draftsToProcess[0].fuelAtStart ?? vehicle.currentFuel);

    console.log(`[recalculateDraftsChain] Starting chain: odometer=${startOdometer}, fuel=${startFuel}`);

    const seasonSettings = await getSeasonSettings();
    const logs: RecalculationLogEntry[] = [];
    const updates: Waybill[] = [];

    for (const current of draftsToProcess) {
        const wb = { ...current };
        const changes: RecalculationChange[] = [];
        const warnings: string[] = [];

        const prevOdoStart = wb.odometerStart;
        const prevFuelStart = wb.fuelAtStart;
        const prevOdoEnd = wb.odometerEnd;
        const prevFuelEnd = wb.fuelAtEnd;
        const prevFuelPlanned = wb.fuelPlanned;

        wb.odometerStart = startOdometer;
        wb.fuelAtStart = startFuel;

        if (wb.odometerStart !== prevOdoStart) {
            changes.push({ field: 'Одометр (нач)', oldVal: prevOdoStart, newVal: wb.odometerStart });
        }
        if (Math.abs(wb.fuelAtStart - prevFuelStart) > 0.01) {
            changes.push({ field: 'Топливо (нач)', oldVal: prevFuelStart.toFixed(2), newVal: wb.fuelAtStart.toFixed(2) });
        }

        const dayMode = wb.date === wb.validTo.split('T')[0] ? 'single' : 'multi';
        const method = wb.calculationMethod || 'by_total';

        const stats = calculateStats(
            wb.routes,
            vehicle,
            seasonSettings,
            wb.date,
            dayMode,
            method
        );

        wb.odometerEnd = wb.odometerStart + stats.distance;
        wb.fuelPlanned = stats.consumption;

        const fuelFilled = wb.fuelFilled || 0;
        // Use Domain Logic
        wb.fuelAtEnd = calculateFuelEnd(wb.fuelAtStart, fuelFilled, stats.consumption);

        if (wb.odometerEnd !== prevOdoEnd) {
            changes.push({ field: 'Одометр (кон)', oldVal: prevOdoEnd ?? '-', newVal: wb.odometerEnd });
        }
        if (Math.abs((wb.fuelPlanned ?? 0) - (prevFuelPlanned ?? 0)) > 0.01) {
            changes.push({ field: 'Норма', oldVal: (prevFuelPlanned ?? 0).toFixed(2), newVal: (wb.fuelPlanned ?? 0).toFixed(2) });
        }
        if (Math.abs((wb.fuelAtEnd ?? 0) - (prevFuelEnd ?? 0)) > 0.01) {
            changes.push({ field: 'Топливо (кон)', oldVal: (prevFuelEnd ?? 0).toFixed(2), newVal: (wb.fuelAtEnd ?? 0).toFixed(2) });
        }

        if ((wb.fuelAtEnd ?? 0) < 0) {
            warnings.push(`Отрицательный остаток топлива: ${wb.fuelAtEnd} л.`);
        }

        updates.push(wb);

        if (changes.length > 0 || warnings.length > 0) {
            logs.push({
                id: wb.id,
                number: wb.number,
                date: wb.date,
                changes,
                warnings
            });
        }

        startOdometer = wb.odometerEnd;
        startFuel = wb.fuelAtEnd ?? 0;
    }

    if (updates.length > 0) {
        await waybillRepository.updateBulk(updates);
        broadcast('waybills');
    }

    return { count: logs.length, logs };
};

// --- Helper: Uniqueness Check ---
const checkNumberUniqueness = async (number: string, date: string, excludeId?: string) => {
    const year = new Date(date).getFullYear();
    const result = await waybillRepository.list({
        pageSize: 1, // We only need to find ONE to prove duplicacy
        filters: {} // No specialized filters here, we use predicate
    });

    // Manual scan because custom filters (year, case-insensitive) aren't supported by basic indexes yet
    // Optimisation: If we had a 'by-year' index, we'd use it. Currently we scan recent.
    // Ideally, waybillRepository should support custom predicates or we scan ALL.
    // For now, let's scan ALL to be safe (as the old code did via predicate).
    const all = await waybillRepository.getAll();

    const duplicate = all.find(w => {
        if (excludeId && w.id === excludeId) return false;
        if (w.number.trim().toLowerCase() !== number.trim().toLowerCase()) return false;
        if (new Date(w.date).getFullYear() !== year) return false;
        if (w.status === WaybillStatus.CANCELLED) return false;
        return true;
    });

    if (duplicate) {
        throw new Error(`Путевой лист с номером "${number}" уже существует в ${year} году.`);
    }
};

export const addWaybill = async (item: Omit<Waybill, 'id'>, context?: { userId?: string }) => {
    console.log('[addWaybill] CALLED with item:', item.number, 'date:', item.date);

    // 1. ЗАЩИТА: Проверяем, не закрыт ли период
    // Мы можем использовать WaybillRules.canEdit, но для создания нужно проверить дату.
    // Пока оставим checkPeriodLock как прямой вызов, так как WaybillRules.canEdit требует объект Waybill.
    // Но можно сделать WaybillRules.isDateBlocked()
    if (await checkPeriodLock(item.date)) {
        throw new Error(`Период для даты ${item.date} закрыт. Создание документа невозможно.`);
    }

    if (!item.calculationMethod) {
        item.calculationMethod = 'by_total';
    }
    if (!item.number || item.number.trim() === '' || item.number === 'БЛАНКОВ НЕТ') {
        item.number = await generateNextNumber('waybill', item.date);
    }

    // Check uniqueness
    await checkNumberUniqueness(item.number, item.date);

    // Resolve assignedDriverUid for cloud sync
    if (item.driverId && !item.assignedDriverUid) {
        const driver = await employeeRepo.getById(item.driverId);
        if (driver?.firebaseUid) {
            item.assignedDriverUid = driver.firebaseUid;
        }
    }

    const wb = await waybillRepository.create(item);

    // Reserve blank if assigned
    if (wb.blankId) {
        await reserveBlank(wb.blankId, wb.id);
    } else {
        // Attempt to find blank by number if manually entered
        const parts = wb.number.split(' ');
        if (parts.length === 2) {
            const [series, numStr] = parts;
            const num = parseInt(numStr, 10);
            if (!isNaN(num)) {
                // Find candidate blank
                const candidates = await blankRepo.list({
                    predicate: b => b.series === series && b.number === num && (b.status === 'issued' || b.status === 'available')
                });

                if (candidates.data.length > 0) {
                    const foundBlank = candidates.data[0];
                    // Link blank to waybill
                    await reserveBlank(foundBlank.id, wb.id);

                    // Update waybill with blank info to be consistent
                    if (!wb.blankId) {
                        wb.blankId = foundBlank.id;
                        wb.blankSeries = foundBlank.series;
                        wb.blankNumber = foundBlank.number;
                        await waybillRepository.update(wb.id, wb);
                    }
                }
            }
        }
    }

    await auditBusiness('waybill.created', { waybillId: wb.id, actorId: context?.userId });
    await recalculateWaybillChain(wb);

    return wb;
};

export const recalculateVehicleStats = async (vehicleId: string) => {
    const vehicle = await vehicleRepo.getById(vehicleId);
    if (!vehicle) return null;

    const allWaybills = await waybillRepository.list({
        filters: { vehicleId },
        pageSize: 1000
    });

    const postedWaybills = allWaybills.filter(w => w.status === WaybillStatus.POSTED);

    if (postedWaybills.length === 0) {
        // No posted waybills - find the earliest draft to get initial values
        const draftWaybills = allWaybills
            .filter(w => w.status === WaybillStatus.DRAFT || w.status === WaybillStatus.SUBMITTED)
            .sort((a, b) => {
                const timeA = new Date(a.validFrom).getTime();
                const timeB = new Date(b.validFrom).getTime();
                if (timeA !== timeB) return timeA - timeB;
                return a.number.localeCompare(b.number);
            });

        if (draftWaybills.length > 0) {
            // Reset to the START values of the earliest draft
            const earliest = draftWaybills[0];
            if (earliest.odometerStart !== undefined && earliest.fuelAtStart !== undefined) {
                vehicle.mileage = earliest.odometerStart;
                vehicle.currentFuel = earliest.fuelAtStart;
                await vehicleRepo.update(vehicleId, vehicle);
            }
        }
        // If no drafts either, leave vehicle stats unchanged
        return vehicle;
    }

    postedWaybills.sort((a, b) => {
        const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateDiff !== 0) return dateDiff;
        const validToDiff = new Date(b.validTo).getTime() - new Date(a.validTo).getTime();
        if (validToDiff !== 0) return validToDiff;
        return b.number.localeCompare(a.number);
    });

    const latest = postedWaybills[0];

    if (latest.odometerEnd !== undefined && latest.fuelAtEnd !== undefined) {
        vehicle.mileage = latest.odometerEnd;
        vehicle.currentFuel = latest.fuelAtEnd;

        await vehicleRepo.update(vehicleId, vehicle);
    }

    return vehicle;
};

// --- Tire Mileage Update Logic (Single) ---
const updateTireMileage = async (waybill: Waybill, isRevert: boolean = false) => {
    if (!waybill.vehicleId) return;

    const allTires = await tireRepo.list({ pageSize: 1000 });
    let mountedTires = allTires.data.filter(t =>
        t.status === 'Mounted' &&
        t.currentVehicleId === waybill.vehicleId
    );

    if (mountedTires.length === 0) return;

    const distance = (waybill.odometerEnd ?? waybill.odometerStart) - waybill.odometerStart;
    if (distance <= 0) return;

    const seasonSettings = await getSeasonSettings();
    const appSettings = await getAppSettings();

    const isWinter = isWinterDate(waybill.date, seasonSettings);
    const method = appSettings?.tireDepreciationMethod || 'usage';

    if (method === 'seasonal') {
        mountedTires = mountedTires.filter(t => {
            if (t.season === 'AllSeason') return true;
            if (isWinter && t.season === 'Winter') return true;
            if (!isWinter && t.season === 'Summer') return true;
            return false;
        });
    }

    const modifier = isRevert ? -1 : 1;
    const tireUpdates: Tire[] = [];
    for (const tire of mountedTires) {
        if (isWinter) {
            tire.winterMileage = (tire.winterMileage || 0) + (distance * modifier);
        } else {
            tire.summerMileage = (tire.summerMileage || 0) + (distance * modifier);
        }
        if ((tire.winterMileage || 0) < 0) tire.winterMileage = 0;
        if ((tire.summerMileage || 0) < 0) tire.summerMileage = 0;

        tireUpdates.push(tire);
    }

    if (tireUpdates.length > 0) {
        await tireRepo.updateBulk(tireUpdates);
    }
};

export const updateWaybill = async (item: Waybill) => {
    console.log('[updateWaybill] CALLED with id:', item.id, 'number:', item.number);
    const oldWb = await waybillRepository.getById(item.id);
    console.log('[updateWaybill] getById result:', oldWb ? 'FOUND' : 'NOT FOUND (null)');
    if (!oldWb) {
        console.error('[updateWaybill] WAYBILL NOT FOUND IN IDB! This ID was likely created via localforage.');
        throw new Error('Путевой лист не найден.');
    }

    console.log('[updateWaybill] Checking domain rules...');
    // --- DOMAIN RULES CHECK ---
    console.log('[updateWaybill] BEFORE checkPeriodLock(oldWb.date)');
    const isPeriodClosedOld = await checkPeriodLock(oldWb.date);
    console.log('[updateWaybill] AFTER checkPeriodLock, closed=', isPeriodClosedOld);

    console.log('[updateWaybill] BEFORE WaybillRules.canEdit');
    const checkResult = WaybillRules.canEdit(oldWb, isPeriodClosedOld);
    console.log('[updateWaybill] AFTER WaybillRules.canEdit, allowed=', checkResult.allowed);

    if (!checkResult.allowed) {
        throw new Error(checkResult.reason || 'Редактирование запрещено.');
    }

    // Resolve assignedDriverUid for cloud sync if driver changed or missing
    if (item.driverId && (!item.assignedDriverUid || item.driverId !== oldWb.driverId)) {
        const driver = await employeeRepo.getById(item.driverId);
        if (driver?.firebaseUid) {
            item.assignedDriverUid = driver.firebaseUid;
        }
    }

    // Дополнительная проверка на перенос в закрытый период
    if (item.date !== oldWb.date) {
        console.log('[updateWaybill] BEFORE checkPeriodLock(item.date)');
        if (await checkPeriodLock(item.date)) {
            throw new Error(`Период ${item.date} закрыт. Перенос документа невозможен.`);
        }
        console.log('[updateWaybill] AFTER checkPeriodLock(item.date)');
    }

    // Check uniqueness if number or year changed
    if (item.number !== oldWb.number || new Date(item.date).getFullYear() !== new Date(oldWb.date).getFullYear()) {
        console.log('[updateWaybill] BEFORE checkNumberUniqueness');
        await checkNumberUniqueness(item.number, item.date, item.id);
        console.log('[updateWaybill] AFTER checkNumberUniqueness');
    }

    console.log('[updateWaybill] BEFORE blank handling');
    // Handle Blank Changes
    if (item.blankId !== oldWb.blankId) {
        if (oldWb.blankId) {
            console.log('[updateWaybill] BEFORE releaseBlank');
            await releaseBlank(oldWb.blankId);
            console.log('[updateWaybill] AFTER releaseBlank');
        }
        if (item.blankId) {
            console.log('[updateWaybill] BEFORE reserveBlank');
            await reserveBlank(item.blankId, item.id);
            console.log('[updateWaybill] AFTER reserveBlank');
        }
    } else if (item.blankId && item.status === WaybillStatus.DRAFT) {
        // Ensure reserved if not already (for existing drafts)
        console.log('[updateWaybill] BEFORE reserveBlank (draft)');
        await reserveBlank(item.blankId, item.id);
        console.log('[updateWaybill] AFTER reserveBlank (draft)');
    } else if (!item.blankId && item.number !== oldWb.number) {
        // Case: Number changed manually, try to link blank
        const parts = item.number.split(' ');
        if (parts.length === 2) {
            const [series, numStr] = parts;
            const num = parseInt(numStr, 10);
            if (!isNaN(num)) {
                console.log('[updateWaybill] BEFORE blankRepo.list');
                const candidates = await blankRepo.list({
                    predicate: b => b.series === series && b.number === num && (b.status === 'issued' || b.status === 'available')
                });
                console.log('[updateWaybill] AFTER blankRepo.list, count=', candidates.data.length);
                if (candidates.data.length > 0) {
                    const foundBlank = candidates.data[0];
                    await reserveBlank(foundBlank.id, item.id);
                    // Update item to reflect found blank
                    item.blankId = foundBlank.id;
                    item.blankSeries = foundBlank.series;
                    item.blankNumber = foundBlank.number;
                }
            }
        }
    }
    console.log('[updateWaybill] AFTER blank handling');

    console.log('[updateWaybill] All checks passed, calling waybillRepository.update()...');

    // --- CHAIN ALIGNMENT (PREDECESSOR SYNC) ---
    // Ensure the updated waybill starts where the previous one ended.
    // This fixes issues where editing a waybill doesn't pull changes from its predecessor.
    const alignedItem = await alignWithPredecessor(item);

    const updatedWb = await waybillRepository.update(item.id, alignedItem);
    if (updatedWb.status === WaybillStatus.POSTED) {
        await recalculateVehicleStats(updatedWb.vehicleId);
    }
    await recalculateWaybillChain(updatedWb);
    return updatedWb;
};

// --- Helper: Align with Predecessor ---
const alignWithPredecessor = async (waybill: Waybill): Promise<Waybill> => {
    // Only align drafts or if explicitly requested (but usually safe for Posted to align too, 
    // though posted implies finalized. Standard logic: Drafts float, Posted anchor.
    // However, if we are editing a Posted waybill (which we allow if period open), it should align too.

    // Find predecessor
    const all = await waybillRepository.list({
        filters: { vehicleId: waybill.vehicleId },
        pageSize: 10000 // optimize?
    });

    // Sort to find position
    const sorted = all.sort((a, b) => {
        const timeA = new Date(a.validFrom).getTime();
        const timeB = new Date(b.validFrom).getTime();
        if (timeA !== timeB) return timeA - timeB;
        return a.number.localeCompare(b.number);
    });

    const currentIndex = sorted.findIndex(w => w.id === waybill.id);
    // If not found (new item?) or first item, nothing to align
    if (currentIndex <= 0) return waybill; // No predecessor

    const predecessor = sorted[currentIndex - 1];

    // Check if we need to align
    const newOdoStart = predecessor.odometerEnd ?? predecessor.odometerStart;
    const newFuelStart = predecessor.fuelAtEnd ?? 0;

    // Check drift
    const odoDiff = Math.abs((waybill.odometerStart ?? 0) - newOdoStart);
    const fuelDiff = Math.abs((waybill.fuelAtStart ?? 0) - newFuelStart);

    if (odoDiff < 0.1 && fuelDiff < 0.01) {
        return waybill; // No change needed
    }

    console.log(`[alignWithPredecessor] Aligning Waybill ${waybill.number}:`,
        `Odo: ${waybill.odometerStart} -> ${newOdoStart}`,
        `Fuel: ${waybill.fuelAtStart} -> ${newFuelStart}`
    );

    // Update start values
    const updated = { ...waybill };
    updated.odometerStart = newOdoStart;
    updated.fuelAtStart = newFuelStart;

    // Recalculate THIS waybill's end stats based on new start
    const vehicle = await vehicleRepo.getById(waybill.vehicleId);
    const seasonSettings = await getSeasonSettings();
    if (vehicle) {
        const dayMode = updated.date === updated.validTo.split('T')[0] ? 'single' : 'multi';
        const method = updated.calculationMethod || 'by_total';
        const stats = calculateStats(
            updated.routes,
            vehicle,
            seasonSettings,
            updated.date,
            dayMode,
            method
        );
        updated.odometerEnd = updated.odometerStart + stats.distance;
        updated.fuelPlanned = stats.consumption;
        updated.fuelAtEnd = calculateFuelEnd(updated.fuelAtStart, updated.fuelFilled || 0, stats.consumption);
    }

    return updated;
};

export const deleteWaybill = async (id: string, markAsSpoiled: boolean) => {
    const wb = await waybillRepository.getById(id);
    if (wb) {
        // --- DOMAIN RULES CHECK ---
        const isPeriodClosed = await checkPeriodLock(wb.date);
        const checkResult = WaybillRules.canEdit(wb, isPeriodClosed);

        if (!checkResult.allowed) {
            throw new Error(checkResult.reason || 'Удаление запрещено.');
        }

        // Try to identify the blank, even if the link was lost (Legacy/Zombie fix)
        let targetBlankId = wb.blankId;

        if (!targetBlankId && wb.number) {
            const parts = wb.number.split(' ');
            if (parts.length === 2) {
                const [series, numStr] = parts;
                const num = parseInt(numStr, 10);
                if (!isNaN(num)) {
                    const candidates = await blankRepo.list({
                        predicate: b => b.series === series && b.number === num
                    });
                    if (candidates.data.length > 0) {
                        const b = candidates.data[0];
                        // Safety: Only pick if it's genuinely linked or "zombie" (reserved but no link)
                        const isLinked = b.reservedByWaybillId === wb.id || b.usedInWaybillId === wb.id;
                        const isZombie = !b.reservedByWaybillId && !b.usedInWaybillId && (b.status === 'reserved' || b.status === 'used' || b.status === 'issued');

                        if (isLinked || isZombie) {
                            targetBlankId = b.id;
                        }
                    }
                }
            }
        }

        if (targetBlankId) {
            if (markAsSpoiled) {
                await markBlankAsSpoiled(targetBlankId, `Удаление черновика ПЛ №${wb.number}`);
            } else {
                await releaseBlank(targetBlankId);
            }
        }
        if (wb.status === WaybillStatus.POSTED) {
            await updateTireMileage(wb, true);
        }
        await waybillRepository.delete(id);
        await auditBusiness('waybill.cancelled', { waybillId: id });
        if (wb.status === WaybillStatus.POSTED) {
            await recalculateVehicleStats(wb.vehicleId);
        }
    }
};

export const getLatestWaybill = async () => {
    const res = await waybillRepository.list({ pageSize: 1, sortBy: 'date', sortDir: 'desc' });
    return res[0] || null;
};

export const getLastWaybillForVehicle = async (vehicleId: string) => {
    const res = await waybillRepository.list({
        filters: { vehicleId },
        pageSize: 100,
        sortBy: 'date',
        sortDir: 'desc'
    });

    const lastRelevant = res.find(w => w.status !== WaybillStatus.CANCELLED);
    return lastRelevant || null;
};

/**
 * Находит путевые листы, пересекающиеся по датам с указанным периодом для данного ТС.
 * Используется для предотвращения дублирования ПЛ.
 */
export const findOverlappingWaybills = async (
    vehicleId: string,
    validFrom: string,
    validTo: string,
    excludeId?: string
): Promise<Waybill[]> => {
    const all = await waybillRepository.list({
        filters: { vehicleId },
        pageSize: 100000
    });
    return all.filter(wb => {
        if (wb.status === WaybillStatus.CANCELLED) return false;
        if (excludeId && wb.id === excludeId) return false;
        // Overlap: startA <= endB AND endA >= startB
        return wb.validFrom <= validTo && wb.validTo >= validFrom;
    });
};

export const getMedicalExamsCount = (w: Waybill): number => {
    const uniqueDates = new Set<string>();
    if (w.routes && w.routes.length > 0) {
        w.routes.forEach(r => {
            const d = r.date ? r.date.split('T')[0] : w.date.split('T')[0];
            if (d) uniqueDates.add(d);
        });
    } else {
        uniqueDates.add(w.date.split('T')[0]);
    }
    return uniqueDates.size;
};

// --- SINGLE ITEM CHANGE STATUS (WRAPPER) ---
export const changeWaybillStatus = async (id: string, status: WaybillStatus, context?: any) => {
    return changeWaybillStatusBulk([id], status, context).then(res => {
        // Return single object to match old signature expectation if possible
        const wb = res.success ? (res as any).updatedWaybills[0] : null;
        if (!wb) throw new Error(res.errors[0] || 'Unknown error');
        return { data: wb };
    });
};

// --- BULK CHANGE STATUS (OPTIMIZED) ---
export const changeWaybillStatusBulk = async (ids: string[], status: WaybillStatus, context?: any) => {
    if (ids.length === 0) return { success: true, updatedWaybills: [], errors: [] };

    // 1. Fetch ALL Data needed
    // Use list with large page size to fetch everything. Filtering happens in memory for speed.
    const [allWaybills, allEmployees, allBlanks, allTires, allVehicles, seasonSettings, appSettings] = await Promise.all([
        waybillRepository.list({ pageSize: 100000 }), // Returns Waybill[]
        employeeRepo.list({ pageSize: 100000 }).then(r => r.data),
        blankRepo.list({ pageSize: 100000 }).then(r => r.data),
        tireRepo.list({ pageSize: 100000 }).then(r => r.data),
        vehicleRepo.list({ pageSize: 10000 }).then(r => r.data),
        getSeasonSettings(),
        getAppSettings()
    ]);

    const waybillsMap = new Map(allWaybills.map(w => [w.id, w]));
    const employeesMap = new Map(allEmployees.map(e => [e.id, e]));
    const blanksMap = new Map(allBlanks.map(b => [b.id, b]));
    const vehiclesMap = new Map(allVehicles.map(v => [v.id, v]));

    // Arrays to hold bulk updates
    const updatesWaybills: Waybill[] = [];
    const updatesEmployees: Employee[] = []; // for fuel balance
    const updatesBlanks: WaybillBlank[] = [];
    const updatesTires: Tire[] = [];

    // Sets to track which vehicles need stats recalc or chain recalc
    const vehiclesToRecalcStats = new Set<string>();
    const vehiclesToRecalcChain = new Map<string, string>(); // vehicleId -> minDate

    // Errors
    const errors: string[] = [];

    // Helper map to aggregate employee balance changes during loop
    const employeeBalanceDeltas = new Map<string, number>();

    // 2. Logic Loop
    for (const id of ids) {
        const wb = waybillsMap.get(id);
        if (!wb) {
            errors.push(`Waybill ${id} not found`);
            continue;
        }

        // ЗАЩИТА: Проверка периода
        if (await checkPeriodLock(wb.date)) {
            errors.push(`ПЛ №${wb.number}: Период закрыт (${wb.date}). Операция отклонена.`);
            continue;
        }

        if (wb.status === status) continue;

        // Clone to modify
        const nextWb = { ...wb };
        const prevStatus = wb.status;

        // LOGIC: POSTING
        if (status === WaybillStatus.POSTED && prevStatus !== WaybillStatus.POSTED) {
            // Blanks
            if (nextWb.blankId) {
                const blank = blanksMap.get(nextWb.blankId);
                if (blank) {
                    blank.status = 'used';
                    blank.usedInWaybillId = nextWb.id;
                    updatesBlanks.push(blank);
                }
            }

            // --- LEDGER INTEGRATION (Refuel & Consumtion) ---
            const vehicle = vehiclesMap.get(nextWb.vehicleId);
            const fuelStockItemId = vehicle?.fuelTypeId || 'FUEL_GENERIC';

            // Helper to ensure tank exists
            // Using a self-invoking async function logic here or we need to await outside?
            // Since we are in an async loop, await is fine.
            let tankLoc = await stockLocationRepository.findByVehicleId(nextWb.vehicleId);
            if (!tankLoc) {
                tankLoc = await stockLocationRepository.create({
                    organizationId: nextWb.organizationId,
                    type: 'VEHICLE_TANK',
                    name: `Tank ${vehicle?.plateNumber || nextWb.vehicleId}`,
                    vehicleId: nextWb.vehicleId
                });
            }

            // 1. REFUELING (Income/Transfer)
            if (nextWb.fuelFilled && nextWb.fuelFilled > 0) {
                // Try to find driver's card
                const card = nextWb.driverId ? await fuelCardService.findActiveByDriver(nextWb.driverId) : null;

                if (card) {
                    // Transfer Card -> Tank
                    await stockLedgerService.createTransfer({
                        fromLocationId: card.stockLocationId,
                        toLocationId: tankLoc.id,
                        stockItemId: fuelStockItemId,
                        quantity: nextWb.fuelFilled,
                        documentType: 'WAYBILL',
                        documentId: nextWb.id,
                        externalRef: `WB:REFUEL:${nextWb.id}`
                    });
                } else {
                    // Unknown Source (Cash/External) -> Income to Tank
                    await stockLedgerService.createIncome({
                        stockLocationId: tankLoc.id,
                        stockItemId: fuelStockItemId,
                        quantity: nextWb.fuelFilled,
                        documentType: 'WAYBILL',
                        documentId: nextWb.id,
                        externalRef: `WB:REFUEL:${nextWb.id}`
                        // Note: ExternalRef collision possible if we use same format? 
                        // Ideally we should distinguish, but documentId assumes only one refuel per WB?
                        // Yes, UI has single fuelFilled field.
                    });
                }
            }

            // 2. CONSUMPTION (Expense)
            // Use fuelPlanned (calculated consumption) if available, or difference? 
            // In multi-user we use calculated consumption from 'stats'.
            // nextWb.fuelPlanned is set during Recalculation which usually happens before Posting/Updating.
            // If it's missing, we might default to 0.
            const consumption = nextWb.fuelPlanned || 0;
            if (consumption > 0) {
                await stockLedgerService.createExpense({
                    stockLocationId: tankLoc.id,
                    stockItemId: fuelStockItemId,
                    quantity: consumption,
                    documentType: 'WAYBILL',
                    documentId: nextWb.id,
                    externalRef: `WB:CONSUME:${nextWb.id}`
                });
            }

            // Status
            nextWb.postedAt = new Date().toISOString();
            nextWb.postedBy = context?.userId;
            nextWb.status = status;


            // Track vehicle
            if (nextWb.vehicleId) vehiclesToRecalcStats.add(nextWb.vehicleId);

            // Tires
            if (nextWb.vehicleId) {
                const distance = (nextWb.odometerEnd ?? nextWb.odometerStart) - nextWb.odometerStart;
                if (distance > 0) {
                    const isWinter = isWinterDate(nextWb.date, seasonSettings);
                    const method = appSettings?.tireDepreciationMethod || 'usage';

                    const mountedTires = allTires.filter(t => t.status === 'Mounted' && t.currentVehicleId === nextWb.vehicleId);

                    for (const tire of mountedTires) {
                        let shouldUpdate = true;
                        if (method === 'seasonal') {
                            if (isWinter && tire.season === 'Summer') shouldUpdate = false;
                            if (!isWinter && tire.season === 'Winter') shouldUpdate = false;
                        }
                        if (shouldUpdate) {
                            if (isWinter) tire.winterMileage = (tire.winterMileage || 0) + distance;
                            else tire.summerMileage = (tire.summerMileage || 0) + distance;

                            // Check if tire already in update list, replace if so (simple linear search ok for small batch)
                            const idx = updatesTires.findIndex(t => t.id === tire.id);
                            if (idx >= 0) updatesTires[idx] = tire;
                            else updatesTires.push(tire);
                        }
                    }
                }
            }
        }
        // LOGIC: REVERT TO DRAFT
        else if (prevStatus === WaybillStatus.POSTED && status === WaybillStatus.DRAFT) {
            // Blanks
            if (nextWb.blankId) {
                const blank = blanksMap.get(nextWb.blankId);
                if (blank) {
                    blank.status = 'issued';
                    blank.usedInWaybillId = null;
                    blank.reservedByWaybillId = null;
                    updatesBlanks.push(blank);
                }
            }
            // Fuel Card (Refund) - REPLACED BY LEDGER VOID
            // if (nextWb.fuelFilled && nextWb.fuelFilled > 0 && nextWb.driverId) { ... }

            // LEDGER: Void transactions
            await stockLedgerService.voidByDocument('WAYBILL', nextWb.id, `Status change: POSTED -> ${status}`);
            // Tires (Revert)
            if (nextWb.vehicleId) {
                const distance = (nextWb.odometerEnd ?? nextWb.odometerStart) - nextWb.odometerStart;
                if (distance > 0) {
                    const isWinter = isWinterDate(nextWb.date, seasonSettings);
                    const method = appSettings?.tireDepreciationMethod || 'usage';
                    const mountedTires = allTires.filter(t => t.status === 'Mounted' && t.currentVehicleId === nextWb.vehicleId);

                    for (const tire of mountedTires) {
                        let shouldUpdate = true;
                        if (method === 'seasonal') {
                            if (isWinter && tire.season === 'Summer') shouldUpdate = false;
                            if (!isWinter && tire.season === 'Winter') shouldUpdate = false;
                        }
                        if (shouldUpdate) {
                            if (isWinter) tire.winterMileage = Math.max(0, (tire.winterMileage || 0) - distance);
                            else tire.summerMileage = Math.max(0, (tire.summerMileage || 0) - distance);

                            const idx = updatesTires.findIndex(t => t.id === tire.id);
                            if (idx >= 0) updatesTires[idx] = tire;
                            else updatesTires.push(tire);
                        }
                    }
                }
            }
            // Status
            nextWb.status = status;
            if (context?.reason) {
                nextWb.notes = (nextWb.notes || '') + `\nCorrection: ${context.reason}`;
            }

            // Track for Stats and Chain Recalc
            if (nextWb.vehicleId) {
                vehiclesToRecalcStats.add(nextWb.vehicleId);
                const currentMin = vehiclesToRecalcChain.get(nextWb.vehicleId);
                if (!currentMin || nextWb.date < currentMin) {
                    vehiclesToRecalcChain.set(nextWb.vehicleId, nextWb.date);
                }
            }
        }
        // LOGIC: CANCEL (full rollback, same as POSTED → DRAFT)
        else if (status === WaybillStatus.CANCELLED) {
            // If the waybill was POSTED, we need to revert all posted effects
            if (prevStatus === WaybillStatus.POSTED) {
                // Blanks: revert used → issued
                if (nextWb.blankId) {
                    const blank = blanksMap.get(nextWb.blankId);
                    if (blank) {
                        blank.status = 'issued';
                        blank.usedInWaybillId = null;
                        blank.reservedByWaybillId = null;
                        updatesBlanks.push(blank);
                    }
                }
                // Fuel Card (Refund) - REPLACED BY LEDGER VOID
                // if (nextWb.fuelFilled && nextWb.fuelFilled > 0 && nextWb.driverId) { ... }

                // LEDGER: Void transactions
                await stockLedgerService.voidByDocument('WAYBILL', nextWb.id, `Status change: POSTED -> ${status}`);
                // Tires (Revert)
                if (nextWb.vehicleId) {
                    const distance = (nextWb.odometerEnd ?? nextWb.odometerStart) - nextWb.odometerStart;
                    if (distance > 0) {
                        const isWinter = isWinterDate(nextWb.date, seasonSettings);
                        const method = appSettings?.tireDepreciationMethod || 'usage';
                        const mountedTires = allTires.filter(t => t.status === 'Mounted' && t.currentVehicleId === nextWb.vehicleId);

                        for (const tire of mountedTires) {
                            let shouldUpdate = true;
                            if (method === 'seasonal') {
                                if (isWinter && tire.season === 'Summer') shouldUpdate = false;
                                if (!isWinter && tire.season === 'Winter') shouldUpdate = false;
                            }
                            if (shouldUpdate) {
                                if (isWinter) tire.winterMileage = Math.max(0, (tire.winterMileage || 0) - distance);
                                else tire.summerMileage = Math.max(0, (tire.summerMileage || 0) - distance);

                                const idx = updatesTires.findIndex(t => t.id === tire.id);
                                if (idx >= 0) updatesTires[idx] = tire;
                                else updatesTires.push(tire);
                            }
                        }
                    }
                }
                // Track for Stats Recalc
                if (nextWb.vehicleId) {
                    vehiclesToRecalcStats.add(nextWb.vehicleId);
                }
            } else if (prevStatus === WaybillStatus.DRAFT || prevStatus === WaybillStatus.SUBMITTED) {
                // For drafts/submitted, just release the blank
                if (nextWb.blankId) {
                    const blank = blanksMap.get(nextWb.blankId);
                    if (blank) {
                        blank.status = 'issued';
                        blank.usedInWaybillId = null;
                        blank.reservedByWaybillId = null;
                        updatesBlanks.push(blank);
                    }
                }
            }

            // Status & Notes
            nextWb.status = status;
            if (context?.reason) {
                nextWb.notes = (nextWb.notes || '') + `\nОтмена: ${context.reason}`;
            } else {
                nextWb.notes = (nextWb.notes || '') + `\nМассовая отмена`;
            }
        } else {
            // Other transitions (Draft <-> Submitted)
            nextWb.status = status;
        }

        updatesWaybills.push(nextWb);
    }

    // 3. Prepare Employee Updates
    for (const [empId, delta] of employeeBalanceDeltas.entries()) {
        const emp = employeesMap.get(empId);
        if (emp && Math.abs(delta) > 0.001) {
            emp.fuelCardBalance = (emp.fuelCardBalance || 0) + delta;
            updatesEmployees.push(emp);
        }
    }

    // 4. Batch Writes
    // We execute these in parallel. This is safer than sequential loops.
    // If one fails, we might have inconsistency, but localforage is generally reliable for bulk ops.
    await Promise.all([
        updatesWaybills.length > 0 ? waybillRepository.updateBulk(updatesWaybills) : Promise.resolve(),
        updatesEmployees.length > 0 ? employeeRepo.updateBulk(updatesEmployees) : Promise.resolve(),
        updatesBlanks.length > 0 ? blankRepo.updateBulk(updatesBlanks) : Promise.resolve(),
        updatesTires.length > 0 ? tireRepo.updateBulk(updatesTires) : Promise.resolve(),
    ]);

    // 5. Post-Processing (Recalcs)

    // Vehicle Stats (Last Posted state)
    for (const vehicleId of vehiclesToRecalcStats) {
        await recalculateVehicleStats(vehicleId);
    }

    // Waybill Chain (Drafts)
    for (const [vehicleId, minDate] of vehiclesToRecalcChain.entries()) {
        await recalculateDraftsChain(vehicleId, minDate);
    }

    // 6. Audit (simplified bulk entry or individual?)
    if (updatesWaybills.length > 0) {
        // Just log the action type generically
        await auditBusiness(`waybill.${status.toLowerCase()}.bulk` as any, {
            count: updatesWaybills.length,
            actorId: context?.userId,
            ids: updatesWaybills.map(w => w.id)
        });
    }

    // Notify UI
    broadcast('waybills');
    broadcast('stock'); // If fuel cards changed
    broadcast('blanks');

    return { success: true, updatedWaybills: updatesWaybills, errors };
};

export const validateBatchCorrection = async (ids: string[]): Promise<{ valid: boolean; error?: string }> => {
    // Helper to get waybills by IDs
    const targetWaybills: Waybill[] = [];
    for (const id of ids) {
        const w = await waybillRepository.getById(id);
        if (w) targetWaybills.push(w);
    }

    if (targetWaybills.length === 0) return { valid: true };

    const vehicleIds = new Set(targetWaybills.map(w => w.vehicleId));

    for (const vehicleId of vehicleIds) {
        if (!vehicleId) continue;

        // Get all POSTED waybills for this vehicle
        const allResult = await waybillRepository.list({
            filters: { vehicleId, status: WaybillStatus.POSTED },
            pageSize: 10000
        });

        // Sort descending (latest first)
        const sortedPosted = allResult.sort((a, b) => {
            const timeA = new Date(a.validFrom).getTime();
            const timeB = new Date(b.validFrom).getTime();
            if (timeA !== timeB) return timeB - timeA; // Descending
            return b.number.localeCompare(a.number);
        });

        const selectedIdsForVehicle = new Set(targetWaybills.filter(w => w.vehicleId === vehicleId).map(w => w.id));

        // Find the oldest selected waybill index in the sorted list (which is sorted Newest -> Oldest)
        // If we select a waybill at index 5, then indices 0..4 must also be selected, 
        // otherwise we are leaving a newer posted waybill active while reverting an older one.

        let maxIndex = -1; // Index of the "oldest" waybill in our selection (highest index in sorted list)

        for (let i = 0; i < sortedPosted.length; i++) {
            if (selectedIdsForVehicle.has(sortedPosted[i].id)) {
                maxIndex = i;
            }
        }

        if (maxIndex !== -1) {
            // Check for gaps (unselected newer waybills)
            for (let i = 0; i < maxIndex; i++) {
                if (!selectedIdsForVehicle.has(sortedPosted[i].id)) {
                    return {
                        valid: false,
                        error: `Нарушение хронологии для ТС (ID: ${vehicleId}). Найден более поздний проведенный ПЛ №${sortedPosted[i].number}, который не выбран. Отмена проведения возможна только последовательно с последнего документа.`
                    };
                }
            }
        }
    }

    return { valid: true };
};
